import C from '../../lib/Config';

/**
 * Sends an audio blob to /voiceOpVisit/capture and returns the structured response.
 * Returns { serviceStatus, errorMsg, data } per the API spec.
 */
export async function captureVoiceOpVisit(patientId, audioBlob, languageCode = 'unknown') {
  const formData = new FormData();
  formData.append('patientId', patientId);
  formData.append('audio', audioBlob, 'voice.webm');
  formData.append('languageCode', languageCode);

  try {
    const response = await fetch(`${C.getUrl()}/voiceOpVisit/capture`, {
      method: 'POST',
      body: formData,
      // Do NOT set Content-Type header; browser will set multipart boundary automatically
      // Authorization header auto-attached by fetchInterceptor.js
    });

    const result = await response.json();
    return result;
  } catch (err) {
    return {
      serviceStatus: 'SYSTEM_FAILURE',
      errorMsg: `Network error: ${err.message}`,
      data: null,
    };
  }
}

/**
 * Maps the API's visit object to a formData-shaped patch + diff list.
 * Returns { patch, diffList, flaggedMedicines }
 * - patch: partial formData object to merge
 * - diffList: array of { label, oldValue, newValue } for overwrite-confirm flow
 * - flaggedMedicines: array of { index, medicine, reason } for visual warnings
 */
export function mapVoiceVisitToFormPatch(apiVisitData, currentFormData, medicinesArr) {
  const patch = {};
  const diffList = [];
  const flaggedMedicines = [];

  // Helper: detect if a field has user-entered data
  const hasUserValue = (val) => val !== '' && val !== null && val !== undefined;

  // ===== VITALS (first record only) =====
  if (apiVisitData.vitals) {
    const apiVitals = apiVisitData.vitals;
    const currentVitals = currentFormData.vitals[0] || {};
    const newVitals = { ...currentVitals };
    let vitalsDiffLabel = null;

    // Map individual vitals fields
    if (apiVitals.height_cm && !hasUserValue(currentVitals.height)) {
      newVitals.height = String(apiVitals.height_cm);
    } else if (apiVitals.height_cm && hasUserValue(currentVitals.height) && currentVitals.height !== String(apiVitals.height_cm)) {
      vitalsDiffLabel = `Height (${currentVitals.height}cm → ${apiVitals.height_cm}cm)`;
    }

    if (apiVitals.weight_kg && !hasUserValue(currentVitals.weight)) {
      newVitals.weight = String(apiVitals.weight_kg);
    } else if (apiVitals.weight_kg && hasUserValue(currentVitals.weight) && currentVitals.weight !== String(apiVitals.weight_kg)) {
      vitalsDiffLabel = vitalsDiffLabel ? vitalsDiffLabel + ', Weight' : `Weight (${currentVitals.weight}kg → ${apiVitals.weight_kg}kg)`;
    }

    if (apiVitals.pulse_bpm && !hasUserValue(currentVitals.pulse)) {
      newVitals.pulse = String(apiVitals.pulse_bpm);
    }
    if (apiVitals.temperature_f && !hasUserValue(currentVitals.temperature)) {
      newVitals.temperature = String(apiVitals.temperature_f);
    }
    if (apiVitals.spo2_percent && !hasUserValue(currentVitals.spo2)) {
      newVitals.spo2 = String(apiVitals.spo2_percent);
    }
    if (apiVitals.respiratory_rate_bpm && !hasUserValue(currentVitals.respiratoryRate)) {
      newVitals.respiratoryRate = String(apiVitals.respiratory_rate_bpm);
    }
    if (apiVitals.blood_glucose_mg_dl && !hasUserValue(currentVitals.bloodGlucose)) {
      newVitals.bloodGlucose = String(apiVitals.blood_glucose_mg_dl);
    }
    if (apiVitals.glucose_test_type && !hasUserValue(currentVitals.glucoseTestType)) {
      newVitals.glucoseTestType = apiVitals.glucose_test_type;
    }

    // Handle BP (combine systolic + diastolic into "Sys/Dia" string)
    if (apiVitals.bp && apiVitals.bp.systolic !== null && apiVitals.bp.diastolic !== null) {
      const bpString = `${apiVitals.bp.systolic}/${apiVitals.bp.diastolic}`;
      if (!hasUserValue(currentVitals.bp)) {
        newVitals.bp = bpString;
      } else if (currentVitals.bp !== bpString) {
        vitalsDiffLabel = vitalsDiffLabel ? vitalsDiffLabel + ', BP' : `BP (${currentVitals.bp} → ${bpString})`;
      }
    }

    if (vitalsDiffLabel) {
      diffList.push({ label: vitalsDiffLabel, oldValue: '(existing)', newValue: '(from voice)' });
    }

    // Set patch vitals
    if (Object.keys(newVitals).length > Object.keys(currentVitals).length ||
        JSON.stringify(newVitals) !== JSON.stringify(currentVitals)) {
      patch.vitals = [newVitals];
    }
  }

  // ===== CHIEF COMPLAINTS =====
  if (apiVisitData.chief_complaints && Array.isArray(apiVisitData.chief_complaints)) {
    const newComplaints = apiVisitData.chief_complaints.map((c) => ({
      complaint: c.complaint || '',
      frequency: c.frequency || '',
      severity: c.severity || '',
      duration: c.duration || '',
      durationValue: c.duration_value || '',
      durationUnit: c.duration_unit || 'Days',
      date: c.date || '',
    }));

    const hasExistingComplaints = currentFormData.complaints.some(
      (c) => c.complaint || c.frequency || c.severity || c.duration
    );

    if (hasExistingComplaints && newComplaints.length > 0) {
      diffList.push({ label: 'Chief Complaints', oldValue: '(existing)', newValue: `(${newComplaints.length} new)` });
    }

    // Append blank row to preserve auto-new-row behavior
    newComplaints.push({
      complaint: '',
      frequency: '',
      severity: '',
      duration: '',
      durationValue: '',
      durationUnit: 'Days',
      date: '',
    });

    patch.complaints = newComplaints;
  }

  // ===== HISTORY OF PRESENT ILLNESS =====
  if (apiVisitData.history_of_present_illness && !hasUserValue(currentFormData.historyOfPresentIllness)) {
    patch.historyOfPresentIllness = apiVisitData.history_of_present_illness;
  } else if (
    apiVisitData.history_of_present_illness &&
    hasUserValue(currentFormData.historyOfPresentIllness) &&
    currentFormData.historyOfPresentIllness !== apiVisitData.history_of_present_illness
  ) {
    diffList.push({
      label: 'History of Present Illness',
      oldValue: '(existing)',
      newValue: '(from voice)',
    });
  }

  // ===== CLINICAL ASSESSMENT =====
  if (apiVisitData.clinical_assessment) {
    const newAssessment = { ...currentFormData.clinicalAssessment };
    let assessmentDiff = null;

    if (apiVisitData.clinical_assessment.status_of_disease && !hasUserValue(currentFormData.clinicalAssessment.statusOfDisease)) {
      newAssessment.statusOfDisease = apiVisitData.clinical_assessment.status_of_disease;
    } else if (
      apiVisitData.clinical_assessment.status_of_disease &&
      currentFormData.clinicalAssessment.statusOfDisease !== apiVisitData.clinical_assessment.status_of_disease
    ) {
      assessmentDiff = 'Status of Disease';
    }

    if (apiVisitData.clinical_assessment.ecog && !hasUserValue(currentFormData.clinicalAssessment.ecog)) {
      newAssessment.ecog = String(apiVisitData.clinical_assessment.ecog);
    }

    if (apiVisitData.clinical_assessment.kps && !hasUserValue(currentFormData.clinicalAssessment.kps)) {
      newAssessment.kps = String(apiVisitData.clinical_assessment.kps);
    }

    if (apiVisitData.clinical_assessment.on_examination && !hasUserValue(currentFormData.clinicalAssessment.onExamination)) {
      newAssessment.onExamination = apiVisitData.clinical_assessment.on_examination;
    }

    if (assessmentDiff) {
      diffList.push({ label: assessmentDiff, oldValue: '(existing)', newValue: '(from voice)' });
    }

    patch.clinicalAssessment = newAssessment;
  }

  // ===== SECONDARY DIAGNOSIS =====
  if (apiVisitData.secondary_diagnosis && Array.isArray(apiVisitData.secondary_diagnosis)) {
    const newDiagnoses = apiVisitData.secondary_diagnosis.map((d) => ({
      diagnosis: d.diagnosis || '',
      duration: d.duration || '',
      comments: d.comments || '',
      date: d.date || '',
      durationValue: d.duration_value || '',
      durationUnit: d.duration_unit || 'Days',
    }));

    const hasExistingDiagnoses = currentFormData.secondaryDiagnosis.some(
      (d) => d.diagnosis || d.duration || d.comments
    );

    if (hasExistingDiagnoses && newDiagnoses.length > 0) {
      diffList.push({ label: 'Secondary Diagnosis', oldValue: '(existing)', newValue: `(${newDiagnoses.length} new)` });
    }

    newDiagnoses.push({
      diagnosis: '',
      duration: '',
      comments: '',
      date: '',
      durationValue: '',
      durationUnit: 'Days',
    });

    patch.secondaryDiagnosis = newDiagnoses;
  }

  // ===== DOCTOR PRESCRIPTION / MEDICINES =====
  if (apiVisitData.doctor_prescription && Array.isArray(apiVisitData.doctor_prescription)) {
    const newPrescriptions = apiVisitData.doctor_prescription.map((med, idx) => {
      const medicine = med.medicine || '';
      let normalizedMedicine = medicine;
      let reason = null;

      // Case-insensitive match against medicinesArr
      const masterMatch = medicinesArr.find(
        (m) => m.name && m.name.toLowerCase().trim() === medicine.toLowerCase().trim()
      );

      if (masterMatch) {
        normalizedMedicine = masterMatch.name; // Normalize to master's casing
      } else if (medicine) {
        reason = `⚠ "${medicine}" will be added to medicine master`;
      }

      const duration = med.duration || (med.duration_value && med.duration_unit ? `${med.duration_value} ${med.duration_unit}` : '');
      const obj = {
        medicine: normalizedMedicine,
        dose: med.dose || '',
        when: med.when || '',
        duration,
        durationValue: med.duration_value || '',
        durationUnit: med.duration_unit || 'Days',
        instructions: med.instructions || '',
        quantity: med.quantity || '',
        unit: med.unit || '',
        date: med.date || '',
      };

      // Check for required fields missing (these cause silent row drop at save)
      if (!obj.medicine || !obj.dose || !obj.when || !obj.duration || !obj.instructions) {
        reason = `⚠ Incomplete (missing: ${[
          !obj.medicine && 'medicine',
          !obj.dose && 'dose',
          !obj.when && 'when',
          !obj.duration && 'duration',
          !obj.instructions && 'instructions',
        ]
          .filter(Boolean)
          .join(', ')})`;
      }

      if (reason) {
        flaggedMedicines.push({ index: idx, medicine: obj.medicine || medicine, reason });
      }

      return obj;
    });

    const hasExistingPrescriptions = currentFormData.prescription.some((p) => p.medicine || p.dose || p.when);

    if (hasExistingPrescriptions && newPrescriptions.length > 0) {
      diffList.push({ label: 'Prescriptions', oldValue: '(existing)', newValue: `(${newPrescriptions.length} new)` });
    }

    newPrescriptions.push({
      medicine: '',
      dose: '',
      when: '',
      duration: '',
      durationValue: '',
      durationUnit: 'Days',
      instructions: '',
      quantity: '',
      unit: '',
      date: '',
    });

    patch.prescription = newPrescriptions;
  }

  // ===== TREATMENT =====
  if (apiVisitData.treatment && Array.isArray(apiVisitData.treatment)) {
    const newTreatments = apiVisitData.treatment.map((t) => ({
      treatmentType: t.treatment_type || '',
      description: t.description || '',
      date: t.date || '',
    }));

    const hasExistingTreatments = currentFormData.treatment.some((t) => t.treatmentType || t.description);

    if (hasExistingTreatments && newTreatments.length > 0) {
      diffList.push({ label: 'Treatment', oldValue: '(existing)', newValue: `(${newTreatments.length} new)` });
    }

    newTreatments.push({
      treatmentType: '',
      description: '',
      date: '',
    });

    patch.treatment = newTreatments;
  }

  // ===== TESTS REQUESTED =====
  if (apiVisitData.tests_requested && Array.isArray(apiVisitData.tests_requested)) {
    const newTests = apiVisitData.tests_requested;
    const hasExistingTests = currentFormData.testsRequested && currentFormData.testsRequested.trim() !== '';

    if (hasExistingTests && newTests.length > 0) {
      diffList.push({ label: 'Tests Requested', oldValue: '(existing)', newValue: `(${newTests.length} new)` });
    }

    // Will be mapped to selectedTests state (synced to testsRequested via useEffect)
    patch.selectedTests = newTests;
  }

  // ===== REFER TO =====
  if (apiVisitData.refer_to && !hasUserValue(currentFormData.referTo)) {
    patch.referTo = apiVisitData.refer_to;
  } else if (apiVisitData.refer_to && currentFormData.referTo !== apiVisitData.refer_to) {
    diffList.push({ label: 'Refer To', oldValue: currentFormData.referTo, newValue: apiVisitData.refer_to });
  }

  // ===== ADVICE NOTES =====
  if (apiVisitData.advice_notes && !hasUserValue(currentFormData.adviceNotes)) {
    patch.adviceNotes = apiVisitData.advice_notes;
  }

  // ===== REVIEW NOTES =====
  if (apiVisitData.review_notes && !hasUserValue(currentFormData.reviewNotes)) {
    patch.reviewNotes = apiVisitData.review_notes;
  }

  // ===== NEXT VISIT DURATION =====
  if (apiVisitData.next_visit) {
    if (apiVisitData.next_visit.duration_value && !hasUserValue(currentFormData.nextVisitDurationValue)) {
      patch.nextVisitDurationValue = String(apiVisitData.next_visit.duration_value);
      patch.nextVisitDurationUnit = apiVisitData.next_visit.duration_unit || 'Days';
      patch.nextVisitDuration = `${apiVisitData.next_visit.duration_value} ${apiVisitData.next_visit.duration_unit || 'Days'}`;
    }
  }

  return { patch, diffList, flaggedMedicines };
}
